import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnDestroy, OutputRefSubscription, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth.service';
import { AiRestrictedHoverDirective } from 'src/app/shared/directives/ai-restricted-hover.directive';
import { ToastService } from '../../../services/toast/toast.service';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { CreateChunkOptionsOverlayData } from 'src/app/views/content/chunk/create-chunk/create-chunk-options-overlay/create-chunk-options-overlay.component';
import { ScreenRecorderOptionsComponent } from './screen-recorder-options.component';
import { firstValueFrom } from 'rxjs';
import { MediaDevicePermissionWatcherService } from 'src/app/media-device-permission-watcher.service';
import { ScreenRecorderService } from './screen-recorder.service';

@Component({
  selector: 'app-screen-recorder',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, MatDialogModule, TranslateModule, AiRestrictedHoverDirective],
  template: `
    @if (isRecording() || isCountingDown()) {
      <button mat-icon-button color="warn" [matTooltip]="'ScreenRecorder.Stop' | translate" (click)="stopRecording()">
        <mat-icon>stop</mat-icon>
      </button>
    } @else {
      @switch (displayMode) {
        @case ('interactive') {
          <div class="interactive-item" [class.disabled]="!canUseAI" (click)="openOptions($event)" [matTooltip]="(canUseAI ? itemText : 'GenerateContent.NoAiAccessTooltip') | translate" [appAiRestrictedHover]="!canUseAI">
            <div class="interactive-item__content">
              <div class="interactive-item__prefix"></div>
              <mat-icon class="interactive-item__icon" aria-hidden="true">{{ itemIcon }}</mat-icon>
              <span class="interactive-item__text">{{ itemText | translate }}</span>
            </div>
          </div>
        }
        @case ('icon') {
          <span [matTooltip]="(canUseAI ? itemText : 'GenerateContent.NoAiAccessTooltip') | translate" [appAiRestrictedHover]="!canUseAI">
            <button mat-icon-button (click)="openOptions($event)" [disabled]="!canUseAI">
              <mat-icon>{{ itemIcon }}</mat-icon>
            </button>
          </span>
        }
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreenRecorderComponent implements OnDestroy {
  @ViewChild('triggerButton') triggerButton!: ElementRef<HTMLButtonElement>;

  @Input() displayMode: 'interactive' | 'icon' = 'interactive';
  @Input() data!: CreateChunkOptionsOverlayData;

  private authService = inject(AuthService);
  canUseAI = this.authService.canUseAI();

  private screenRecorderService = inject(ScreenRecorderService);
  isRecording = this.screenRecorderService.isRecording;
  isCountingDown = this.screenRecorderService.isCountingDown;

  itemText = 'ScreenRecorder.Tooltip.Record';
  itemIcon = 'monitor';

  private startSubscription: OutputRefSubscription | null = null;

  private overlayService = inject(DynamicOverlayService);
  private toast = inject(ToastService);
  private permissionWatcher = inject(MediaDevicePermissionWatcherService);

  ngOnDestroy(): void {
    this.startSubscription?.unsubscribe();
  }

  async openOptions(event: MouseEvent): Promise<void> {
    if (!this.canUseAI) return;
    event.stopPropagation();

    let micAccess = this.permissionWatcher.microphonePermissionState;
    if (micAccess === 'prompt') {
      const granted = await this.permissionWatcher.requestMicrophoneAccess();
      if (!granted) {
        return;
      }
      micAccess = 'granted';
    }

    if (micAccess === 'denied') {
      this.toast.showDevicePermissionError();
      return;
    }

    const { componentInstance } = this.overlayService.open(this.data.triggerRef, ScreenRecorderOptionsComponent);

    this.startSubscription = componentInstance.start.subscribe((micId) => {
      this.overlayService.close();
      this.screenRecorderService.startScreenCapture(micId, this.data);
    });
  }

  stopRecording(): void {
    this.screenRecorderService.stopRecording();
  }
}
